<template>
  <section class="philosophy-section" aria-labelledby="philosophy-title">
    <div class="philosophy-noise" aria-hidden="true"></div>

    <v-container>
      <div class="glass-card">
        <!-- Quote mark -->
        <div class="quote-mark" aria-hidden="true">"</div>

        <p class="phil-eyebrow">{{ $t('about.philosophyLabel') }}</p>

        <h2 id="philosophy-title" class="phil-title">
          {{ $t('about.philosophyTitle') }}
        </h2>

        <div class="phil-divider" aria-hidden="true"></div>

        <p class="phil-body">{{ $t('about.philosophyBody') }}</p>

        <!-- Decorative dots -->
        <div class="glass-dots" aria-hidden="true">
          <span class="dot dot--blue"></span>
          <span class="dot dot--purple"></span>
          <span class="dot dot--red"></span>
        </div>
      </div>
    </v-container>
  </section>
</template>

<script>
export default {
  name: 'Philosophy'
}
</script>

<style lang="scss" scoped>
// ── Brand palette ─────────────────────────────────────────────────────
$blue:   #3949ab;
$purple: #7c3aed;
$red:    #e53935;

// ── Section ───────────────────────────────────────────────────────────
// Aligned with the site's primary palette (deep indigo/navy) rather than
// an arbitrary near-black, so it feels like part of the brand system.
.philosophy-section {
  position: relative;
  padding: 140px 0;
  overflow: hidden;
  background: linear-gradient(158deg, #1a237e 0%, #283593 45%, #4a148c 100%);

  .theme--dark & {
    background: linear-gradient(158deg, #0d1257 0%, #1a1f6e 45%, #2d0e5a 100%);
  }
}

// ── Noise texture overlay ─────────────────────────────────────────────
.philosophy-noise {
  position: absolute;
  inset: 0;
  opacity: 0.035;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E");
  background-size: 200px 200px;
  pointer-events: none;
}

// ── Glassmorphism card ────────────────────────────────────────────────
.glass-card {
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 32px;
  padding: 80px 64px;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.04) inset,
    0 0 100px rgba($blue, 0.12),
    0 40px 80px rgba(0, 0, 0, 0.4);
}

// ── Giant quote mark ──────────────────────────────────────────────────
.quote-mark {
  position: absolute;
  top: 16px;
  inset-inline-start: 32px;
  font-size: 7rem;
  line-height: 1;
  font-family: Georgia, serif;
  background: linear-gradient(135deg, $blue, $purple);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  opacity: 0.4;
  pointer-events: none;
  user-select: none;
}

// ── Eyebrow ───────────────────────────────────────────────────────────
.phil-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.38);
  margin-bottom: 20px;
}

// ── Title ─────────────────────────────────────────────────────────────
.phil-title {
  font-size: clamp(1.75rem, 4vw, 2.75rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.2;
  color: #fff;
  margin-bottom: 28px;
}

// ── Gradient divider ──────────────────────────────────────────────────
.phil-divider {
  width: 64px;
  height: 3px;
  margin: 0 auto 32px;
  border-radius: 2px;
  background: linear-gradient(90deg, $blue, $purple, $red);
}

// ── Body text ─────────────────────────────────────────────────────────
.phil-body {
  font-size: clamp(1rem, 1.8vw, 1.14rem);
  line-height: 1.92;
  color: rgba(255, 255, 255, 0.62);
  margin: 0;
  max-width: 640px;
  margin-inline: auto;
}

// ── Decorative dots ───────────────────────────────────────────────────
.glass-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 48px;
}

.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  opacity: 0.6;

  &--blue   { background: $blue; }
  &--purple { background: $purple; width: 14px; height: 14px; opacity: 0.8; }
  &--red    { background: $red; }
}

// ── Responsive ────────────────────────────────────────────────────────
@media (max-width: 768px) {
  .philosophy-section {
    padding: 90px 0;
  }

  .glass-card {
    padding: 52px 28px;
    border-radius: 22px;
  }

  .quote-mark {
    font-size: 4rem;
    top: 12px;
    inset-inline-start: 20px;
  }
}

@media (max-width: 480px) {
  .glass-card {
    padding: 44px 20px;
  }
}
</style>
